import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Loader2, Send, Eye, Users, ChevronDown, CheckCircle2, AlertCircle } from "lucide-react";
import { Card } from "../shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
const prevMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

export default function DigestPanel() {
  const [month, setMonth] = useState(prevMonth);
  const [year, setYear] = useState(prevMonthYear);
  const [investors, setInvestors] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [loading, setLoading] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/investors`, { headers }).then(res => {
      setInvestors(res.data.investors || []);
    }).catch(() => {});
  }, []);

  const previewDigest = useCallback(async (userId, name) => {
    setLoading(`preview-${userId}`);
    setPreviewHtml(null);
    try {
      const res = await axios.get(`${API}/digest/preview/${userId}?month=${month}&year=${year}`, { headers });
      setPreviewHtml(res.data.html);
      setPreviewName(name);
    } catch (err) {
      alert(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(null);
    }
  }, [month, year]);

  const sendSingle = useCallback(async (userId, name) => {
    setLoading(`send-${userId}`);
    setSendResult(null);
    try {
      const res = await axios.post(`${API}/digest/send/${userId}?month=${month}&year=${year}`, {}, { headers });
      setSendResult({ type: "success", message: `Digest ${res.data.status === "sent" ? "sent to" : "prepared for"} ${name} (${res.data.email})` });
    } catch (err) {
      setSendResult({ type: "error", message: err.response?.data?.detail || "Send failed" });
    } finally {
      setLoading(null);
    }
  }, [month, year]);

  const sendAll = useCallback(async () => {
    setLoading("send-all");
    setSendResult(null);
    try {
      const res = await axios.post(`${API}/digest/send-all?month=${month}&year=${year}`, {}, { headers });
      setSendResult({ type: "success", message: `Digests sent: ${res.data.sent}, Skipped: ${res.data.skipped}` });
    } catch (err) {
      setSendResult({ type: "error", message: err.response?.data?.detail || "Batch send failed" });
    } finally {
      setLoading(null);
    }
  }, [month, year]);

  return (
    <div className="space-y-6" data-testid="digest-panel">
      {/* Header + Period */}
      <Card hover={false}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#222] uppercase tracking-wider" data-testid="digest-title">Investor Performance Digests</h3>
            <p className="text-xs text-[#888] mt-1">Send branded monthly performance emails to investors</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <select value={month} onChange={e => setMonth(Number(e.target.value))}
                className="appearance-none px-3 py-1.5 pr-8 rounded-lg border border-[#e8e0d0] text-xs font-medium bg-white outline-none"
                data-testid="digest-month-select">
                {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#999] pointer-events-none" />
            </div>
            <div className="relative">
              <select value={year} onChange={e => setYear(Number(e.target.value))}
                className="appearance-none px-3 py-1.5 pr-8 rounded-lg border border-[#e8e0d0] text-xs font-medium bg-white outline-none"
                data-testid="digest-year-select">
                {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#999] pointer-events-none" />
            </div>
            <button onClick={sendAll} disabled={!!loading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#022c22] text-[#e5c98b] text-xs font-medium hover:bg-[#064e3b] disabled:opacity-40 transition-colors"
              data-testid="send-all-digests">
              {loading === "send-all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send All
            </button>
          </div>
        </div>
      </Card>

      {/* Status Banner */}
      {sendResult && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-medium ${
            sendResult.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
          }`} data-testid="digest-status">
          {sendResult.type === "success" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {sendResult.message}
        </motion.div>
      )}

      {/* Investor List */}
      <Card hover={false}>
        <h4 className="text-xs font-bold text-[#222] uppercase tracking-wider mb-4">Investors ({investors.length})</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {investors.map(inv => (
            <div key={inv.user_id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#faf7f2] transition-colors" data-testid={`digest-investor-${inv.user_id}`}>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-[#222]">{inv.full_name || inv.email}</span>
                <span className="text-[10px] text-[#999] ml-2">{inv.email}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => previewDigest(inv.user_id, inv.full_name || inv.email)}
                  disabled={!!loading}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e8e0d0] text-[10px] font-medium text-[#666] hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-40 transition-colors"
                  data-testid={`preview-digest-${inv.user_id}`}>
                  {loading === `preview-${inv.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                  Preview
                </button>
                <button onClick={() => sendSingle(inv.user_id, inv.full_name || inv.email)}
                  disabled={!!loading}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-700 text-white text-[10px] font-medium hover:bg-emerald-600 disabled:opacity-40 transition-colors"
                  data-testid={`send-digest-${inv.user_id}`}>
                  {loading === `send-${inv.user_id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                  Send
                </button>
              </div>
            </div>
          ))}
          {investors.length === 0 && (
            <p className="text-xs text-[#999] text-center py-4 italic">No investors found</p>
          )}
        </div>
      </Card>

      {/* Email Preview */}
      {previewHtml && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card hover={false}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-xs font-bold text-[#222] uppercase tracking-wider">Email Preview</span>
                <span className="text-xs text-[#999] ml-2">for {previewName}</span>
              </div>
              <button onClick={() => setPreviewHtml(null)}
                className="px-3 py-1 text-xs rounded-lg border border-[#e8e0d0] text-[#666] hover:border-red-300 hover:text-red-500 transition-colors"
                data-testid="close-digest-preview">Close</button>
            </div>
            <div className="rounded-xl overflow-hidden border border-[#e8e0d0]" data-testid="digest-preview-content"
              dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </Card>
        </motion.div>
      )}
    </div>
  );
}
